import { AuthenticationResponseJSON, browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { SentryTeams } from '@mews/enums';
import { captureWithSentry, LocalizedText, showErrorAlert, useFeatureFlag } from '@mews/framework';
import {
    GeneralArea,
    GeneralAssertionOptionResult,
    GeneralPublicKeyCredentialType,
    GeneralSignInMethod,
    useRepositoryMutation,
} from '@mews-commander/data-access';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import {
    checkForConsent,
    checkForForced2FA,
    getDeviceFingerprintPayload,
    INITIAL_SIGN_IN_PARAMS,
    initializeUserSession,
} from '../../../ducks/actions';

export const useHasPasskeySupport = () => {
    const passkeySignInUiEnabled = useFeatureFlag(FeatureFlag.passkeySignInUiEnabled);
    return passkeySignInUiEnabled && browserSupportsWebAuthn();
};

export const useSignInWithPasskey = ({ setIsCheckingPasskey }: { setIsCheckingPasskey: (v: boolean) => void }) => {
    const dispatch = useCommanderThunkDispatch();
    const { mutateAsync } = useRepositoryMutation('generalUsers', 'signIn');

    const signIn = async ({
        assertionResponse,
        email,
    }: {
        assertionResponse: AuthenticationResponseJSON;
        email: string;
    }) =>
        mutateAsync({
            ...INITIAL_SIGN_IN_PARAMS,
            application: GeneralArea.Commander,
            identifier: email,
            assertionResponse: {
                ...assertionResponse,
                type: GeneralPublicKeyCredentialType.PublicKey,
            },
            method: GeneralSignInMethod.Passkey,
            device: getDeviceFingerprintPayload(),
        })
            .then(async (data) => dispatch(checkForConsent(data)))
            .then(async (data) => dispatch(checkForForced2FA(data)))
            .then(async (data) => dispatch(initializeUserSession(data)))
            // eslint-disable-next-line @mews/no-empty-catch
            .catch(() => {
                // Mute errors from promise rejections in dispatch calls.
                // Mutation errors are handled in the mutation itself.
            });

    return async ({ nameOrEmail, passkeyInfo }: { nameOrEmail: string; passkeyInfo: GeneralAssertionOptionResult }) => {
        setIsCheckingPasskey(true);
        const options = await getPasskeySignInOptions({ options: passkeyInfo });

        if (options) {
            return signIn({ email: nameOrEmail, assertionResponse: options });
        }

        setIsCheckingPasskey(false);
    };
};

const getPasskeySignInOptions = async ({ options }: { options: GeneralAssertionOptionResult }) => {
    const tryPasskeyAuthentication = captureWithSentry({
        fn: async () =>
            await startAuthentication({
                // @ts-expect-error incorrect type in API TODO DX-5802
                optionsJSON: options,
            }),
        onError: () => {
            showErrorAlert({
                title: <LocalizedText textKey={TextKey.LoginWithPasskeyErrorTitle} />,
                description: <LocalizedText textKey={TextKey.LoginWithPasskeyErrorDescription} />,
            });
        },
        team: SentryTeams.Users,
    });

    return tryPasskeyAuthentication();
};
